import {
  charityService,
  charityVehicleService,
  standardDonationService,
} from '@services'
import {
  Address,
  ManifestItem,
  ManifestItemType,
  Report,
  StandardDonation,
  StandardDonationStateParams,
} from '@types'
import { StateCreator } from 'zustand'
import { DonationWithManifestIndex, RouteEffects, RouteStore } from './types'

/**
 * Extracts an Address from a ManifestItem's flat address fields.
 */
const extractAddress = (item: ManifestItem): Address | null => {
  const { street, secondary, city, state, zip, lat, lng } = item
  if (!street && !city && lat == null && lng == null) return null
  return { street, secondary, city, state, zip, lat, lng }
}

/**
 * Sorts donations by manifest order and assigns manifest indices.
 * Filters out ADDRESS-type items (dispatch/return) and numbers donation stops sequentially.
 */
const sortDonationsByManifest = (
  donations: StandardDonation[],
  manifest: ManifestItem[]
): DonationWithManifestIndex[] => {
  // Filter out ADDRESS items and build manifest index map with sequential numbering
  const manifestIndexMap = new Map<number, number>()
  let stopNumber = 1
  manifest.forEach((item) => {
    if (item.type !== ManifestItemType.ADDRESS && item.id != null) {
      manifestIndexMap.set(item.id, stopNumber++)
    }
  })

  // Sort donations by manifest order
  const sortedDonations = [...donations].sort((a, b) => {
    const indexA = manifestIndexMap.get(a.id) ?? Number.MAX_SAFE_INTEGER
    const indexB = manifestIndexMap.get(b.id) ?? Number.MAX_SAFE_INTEGER
    return indexA - indexB
  })

  // Return donations with their manifest index
  return sortedDonations.map((donation, index) => ({
    donation,
    manifestIndex: manifestIndexMap.get(donation.id) ?? index + 1,
  }))
}

export const createRouteEffects: StateCreator<
  RouteStore,
  [],
  [],
  RouteEffects
> = (set, get) => ({
  fetchCharity: async (charityId: number) => {
    if (get().loading) return
    set({ loading: true, error: null })
    try {
      const charity = await charityService.get(charityId)
      set({ charity, loading: false })
    } catch {
      set({ error: 'Failed to fetch charity', loading: false })
    }
  },

  fetchVehicles: async (charityId: number, userEmail?: string) => {
    if (get().vehiclesLoading) return
    set({ vehiclesLoading: true, error: null })
    try {
      const response = await charityVehicleService.list({
        charity_id: charityId,
      })
      set({ vehicles: response.data, vehiclesLoading: false })
      if (get().selectedVehicleId === null && response.data.length > 0) {
        const matchingVehicle = userEmail
          ? response.data.find((v) => v.driver_contact?.email === userEmail)
          : null
        set({
          selectedVehicleId: matchingVehicle?.id ?? response.data[0].id,
        })
      }
    } catch {
      set({ error: 'Failed to fetch vehicles', vehiclesLoading: false })
    }
  },

  fetchDonations: async () => {
    const { selectedVehicleId, selectedDate, donationsLoading } = get()
    if (!selectedVehicleId || !selectedDate || donationsLoading) return

    set({ donationsLoading: true, error: null })

    try {
      const standardDonationsResponse = await standardDonationService.list({
        charity_vehicle_id: selectedVehicleId,
        date: selectedDate,
        active: true,
        order: 'created_at',
        order_direction: 'desc',
      })
      const manifest = await charityVehicleService.manifest.get(
        selectedVehicleId,
        selectedDate
      )
      const donations = standardDonationsResponse.data
      const sortedDonations = sortDonationsByManifest(donations, manifest)

      // Extract dispatch (first) and return (last) addresses from manifest
      const firstItem = manifest[0]
      const lastItem = manifest[manifest.length - 1]
      const dispatchAddress =
        firstItem?.type === ManifestItemType.ADDRESS
          ? extractAddress(firstItem)
          : null
      const returnAddress =
        lastItem?.type === ManifestItemType.ADDRESS && lastItem !== firstItem
          ? extractAddress(lastItem)
          : null

      set({
        donations,
        manifest,
        sortedDonations,
        dispatchAddress,
        returnAddress,
        donationsLoading: false,
      })
    } catch {
      set({ error: 'Failed to fetch donations', donationsLoading: false })
    }
  },

  fetchDonation: async (donationId: number) => {
    const { currentDonationLoading } = get()
    if (currentDonationLoading) return

    set({
      currentDonation: null,
      currentDonationLoading: true,
      error: null,
    })

    try {
      const donation = await standardDonationService.get(donationId)
      set({ currentDonation: donation, currentDonationLoading: false })

      const currentDonations = get().donations
      const index = currentDonations.findIndex((d) => d.id === donationId)
      if (index !== -1) {
        const updatedDonations = [...currentDonations]
        updatedDonations[index] = donation
        const sortedDonations = sortDonationsByManifest(
          updatedDonations,
          get().manifest
        )
        set({ donations: updatedDonations, sortedDonations })
      }
    } catch {
      set({
        error: 'Failed to fetch donation',
        currentDonationLoading: false,
      })
    }
  },

  updateDonationState: async (
    donationId: number,
    state: StandardDonationStateParams
  ) => {
    try {
      const updatedDonation = await standardDonationService.state(
        donationId,
        state
      )

      const currentDonations = get().donations
      const index = currentDonations.findIndex((d) => d.id === donationId)
      if (index !== -1) {
        const updatedDonations = [...currentDonations]
        updatedDonations[index] = updatedDonation
        const sortedDonations = sortDonationsByManifest(
          updatedDonations,
          get().manifest
        )
        set({ donations: updatedDonations, sortedDonations })
      }

      if (get().currentDonation?.id === donationId) {
        set({ currentDonation: updatedDonation })
      }

      return updatedDonation
    } catch {
      set({ error: 'Failed to update donation state' })
      return null
    }
  },

  submitReport: async (donationId: number, report: Report) => {
    try {
      const updatedDonation = await standardDonationService.update(donationId, {
        report,
      })

      const currentDonations = get().donations
      const index = currentDonations.findIndex((d) => d.id === donationId)
      if (index !== -1) {
        const updatedDonations = [...currentDonations]
        updatedDonations[index] = updatedDonation
        const sortedDonations = sortDonationsByManifest(
          updatedDonations,
          get().manifest
        )
        set({ donations: updatedDonations, sortedDonations })
      }

      if (get().currentDonation?.id === donationId) {
        set({ currentDonation: updatedDonation })
      }

      return updatedDonation
    } catch {
      set({ error: 'Failed to submit report' })
      return null
    }
  },
})
